import React, { useState, useEffect } from 'react';
import api from '../../services/api';

const LEVEL_CONFIG = {
  0: { label: 'Pendiente',  color: '#7a7060', bg: '#f0ebe2', icon: '⏳' },
  1: { label: 'Prueba',     color: '#b45309', bg: '#fef3e2', icon: '🔰' },
  2: { label: 'Activo',     color: '#1a4e8c', bg: '#eaf1fb', icon: '✅' },
  3: { label: 'Senior',     color: '#2d6a4f', bg: '#eaf4ef', icon: '⭐' },
  4: { label: 'Élite',      color: '#7c3aed', bg: '#f5f0ff', icon: '👑' },
};

const RISK_CONFIG = {
  HIGH:   { color: '#9b2c5e', bg: '#faeef4', label: '🔴 Alto riesgo' },
  MEDIUM: { color: '#b45309', bg: '#fef3e2', label: '🟡 Riesgo medio' },
  LOW:    { color: '#1a4e8c', bg: '#eaf1fb', label: '🔵 Riesgo bajo' },
  CLEAN:  { color: '#2d6a4f', bg: '#eaf4ef', label: '🟢 Sin alertas' },
};

function TrustBadge({ level }) {
  const cfg = LEVEL_CONFIG[level] || LEVEL_CONFIG[0];
  return (
    <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: cfg.bg, color: cfg.color }}>
      {cfg.icon} {cfg.label}
    </span>
  );
}

function ScoreBar({ score, max = 100 }) {
  const pct = Math.min(100, (score / max) * 100);
  const color = pct > 70 ? '#2d6a4f' : pct > 40 ? '#b45309' : '#9b2c5e';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div style={{ flex: 1, height: 6, background: '#f0ebe2', borderRadius: 3, overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 3, transition: 'width .3s' }} />
      </div>
      <span style={{ fontSize: 12, fontWeight: 700, color, minWidth: 32 }}>{score}</span>
    </div>
  );
}

export default function AdminTrust() {
  const [tab, setTab] = useState('agents');
  const [agents, setAgents] = useState([]);
  const [anomalies, setAnomalies] = useState([]);
  const [network, setNetwork] = useState({ nodes: [], edges: [] });
  const [selected, setSelected] = useState(null);
  const [selectedScore, setSelectedScore] = useState(null);
  const [eligibility, setEligibility] = useState(null);
  const [auditLog, setAuditLog] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [agentsRes, anomaliesRes, networkRes] = await Promise.all([
        api.get('/api/v1/agents/'),
        api.get('/api/v1/trust/anomalies').catch(() => ({ data: [] })),
        api.get('/api/v1/trust/network').catch(() => ({ data: { nodes: [], edges: [] } })),
      ]);
      setAgents(Array.isArray(agentsRes.data) ? agentsRes.data : []);
      setAnomalies(Array.isArray(anomaliesRes.data) ? anomaliesRes.data : []);
      setNetwork(networkRes.data || { nodes: [], edges: [] });
    } catch {}
    finally { setLoading(false); }
  };

  const selectAgent = async (agent) => {
    setSelected(agent);
    try {
      const [scoreRes, auditRes] = await Promise.all([
        api.get(`/api/v1/trust/score/${agent.id}`),
        api.get(`/api/v1/trust/audit/${agent.id}`),
      ]);
      setSelectedScore(scoreRes.data);
      setAuditLog(Array.isArray(auditRes.data) ? auditRes.data : []);

      const nextLevel = (agent.trust_level || 0) + 1;
      if (nextLevel <= 4) {
        const eligRes = await api.get(`/api/v1/trust/eligibility/${agent.id}/${nextLevel}`);
        setEligibility(eligRes.data);
      }
    } catch {}
  };

  const handlePromote = async () => {
    if (!selected || !window.confirm(`¿Promover a ${selected.full_name} al siguiente nivel?`)) return;
    try {
      await api.post(`/api/v1/trust/promote/${selected.id}`);
      await loadAll();
      await selectAgent({ ...selected, trust_level: (selected.trust_level || 0) + 1 });
      alert('✅ Agente promovido');
    } catch (e) {
      alert(e.response?.data?.detail || 'Error al promover');
    }
  };

  const handleDemote = async () => {
    const reason = window.prompt('Motivo de la degradación:');
    if (!reason) return;
    try {
      await api.post(`/api/v1/trust/demote/${selected.id}?reason=${encodeURIComponent(reason)}`);
      await loadAll();
      alert('✅ Nivel reducido');
    } catch (e) {
      alert(e.response?.data?.detail || 'Error');
    }
  };

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ fontFamily: 'Georgia,serif', fontSize: 26, margin: '0 0 4px' }}>Sistema de Confianza</h1>
        <p style={{ fontSize: 13, color: '#7a7060' }}>Guarapo Trust System (GTS) · Verificación multicapa de agentes</p>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 12, marginBottom: 20 }}>
        {Object.entries(LEVEL_CONFIG).map(([level, cfg]) => {
          const count = agents.filter(a => (a.trust_level || 0) === parseInt(level)).length;
          return (
            <div key={level} style={{ background: cfg.bg, borderRadius: 12, padding: '14px 16px', textAlign: 'center' }}>
              <div style={{ fontSize: 22, marginBottom: 4 }}>{cfg.icon}</div>
              <div style={{ fontFamily: 'Georgia,serif', fontSize: 24, color: cfg.color, fontWeight: 700 }}>{count}</div>
              <div style={{ fontSize: 11, color: cfg.color, fontWeight: 600 }}>{cfg.label}</div>
            </div>
          );
        })}
      </div>

      {/* Anomaly alert */}
      {anomalies.length > 0 && (
        <div style={{ background: '#faeef4', border: '1px solid #f4b8c8', borderRadius: 12, padding: '12px 18px', marginBottom: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: '#9b2c5e' }}>
            🔴 {anomalies.length} agente(s) con alertas de seguridad
          </div>
          <button onClick={() => setTab('anomalies')} style={{ padding: '6px 14px', borderRadius: 8, border: 'none', background: '#9b2c5e', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
            Ver alertas
          </button>
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, background: '#f0ebe2', borderRadius: 10, padding: 4, marginBottom: 20, width: 'fit-content' }}>
        {[['agents','👥 Agentes'],['anomalies','🔴 Alertas'],['network','🕸️ Red'],['audit','📋 Auditoría']].map(([t,l]) => (
          <button key={t} onClick={() => setTab(t)} style={{ padding: '8px 16px', borderRadius: 7, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600, background: tab===t?'#fff':'transparent', color: tab===t?'#0f0d0b':'#7a7060' }}>
            {l}
          </button>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: selected ? '1fr 380px' : '1fr', gap: 18 }}>

        {/* Agents list */}
        {tab === 'agents' && (
          <div>
            {loading ? (
              <div style={{ padding: 40, textAlign: 'center', color: '#7a7060' }}>Cargando...</div>
            ) : agents.map((agent, i) => (
              <div key={i} onClick={() => selectAgent(agent)}
                style={{ background: '#fff', border: `1px solid ${selected?.id === agent.id ? '#c94a1e' : '#e2dbd0'}`, borderRadius: 12, padding: '14px 18px', marginBottom: 8, cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>{agent.full_name}</div>
                  <div style={{ fontSize: 12, color: '#7a7060', marginBottom: 6 }}>{agent.code} · {agent.province || agent.city || agent.country}</div>
                  <TrustBadge level={agent.trust_level || 0} />
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontFamily: 'Georgia,serif', fontSize: 18 }}>{agent.total_delivered || 0}</div>
                  <div style={{ fontSize: 11, color: '#7a7060' }}>entregas</div>
                  {agent.rating && <div style={{ color: '#f59e0b', fontSize: 12, marginTop: 2 }}>{'★'.repeat(Math.round(agent.rating))} {parseFloat(agent.rating).toFixed(1)}</div>}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Anomalies */}
        {tab === 'anomalies' && (
          <div>
            {anomalies.length === 0 ? (
              <div style={{ background: '#eaf4ef', borderRadius: 14, padding: 48, textAlign: 'center' }}>
                <div style={{ fontSize: 40, marginBottom: 12 }}>✅</div>
                <div style={{ fontFamily: 'Georgia,serif', fontSize: 18 }}>Sin alertas de seguridad</div>
              </div>
            ) : anomalies.map((agent, i) => {
              const riskCfg = RISK_CONFIG[agent.risk_level] || RISK_CONFIG.LOW;
              return (
                <div key={i} onClick={() => selectAgent(agent)}
                  style={{ background: '#fff', border: `1px solid ${riskCfg.color}40`, borderRadius: 12, padding: '16px 18px', marginBottom: 8, cursor: 'pointer' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 14 }}>{agent.full_name}</div>
                      <div style={{ fontSize: 11, color: '#7a7060' }}>{agent.code}</div>
                    </div>
                    <span style={{ padding: '4px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: riskCfg.bg, color: riskCfg.color }}>{riskCfg.label}</span>
                  </div>
                  {agent.flags.map((flag, j) => (
                    <div key={j} style={{ padding: '6px 10px', borderRadius: 8, background: flag.severity === 'HIGH' ? '#faeef4' : '#fef3e2', marginBottom: 4, fontSize: 12, color: flag.severity === 'HIGH' ? '#9b2c5e' : '#b45309' }}>
                      <b>{flag.type}</b> — {flag.detail}
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        )}

        {/* Network */}
        {tab === 'network' && (
          <div style={{ background: '#fff', border: '1px solid #e2dbd0', borderRadius: 14, padding: 24 }}>
            <div style={{ fontFamily: 'Georgia,serif', fontSize: 16, marginBottom: 16 }}>Red de avales</div>
            {network.edges.length === 0 ? (
              <div style={{ textAlign: 'center', color: '#7a7060', padding: 40 }}>
                <div style={{ fontSize: 32, marginBottom: 12 }}>🕸️</div>
                <div>Sin avales registrados todavía</div>
              </div>
            ) : (
              <div>
                {network.edges.map((edge, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderBottom: '1px solid #f0ebe2', fontSize: 13 }}>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontWeight: 700 }}>{edge.endorser_code}</div>
                      <TrustBadge level={edge.endorser_level || 0} />
                    </div>
                    <div style={{ color: '#c94a1e', fontSize: 18 }}>→</div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontWeight: 700 }}>{edge.endorsed_code}</div>
                      <TrustBadge level={edge.endorsed_level || 0} />
                    </div>
                    <div style={{ marginLeft: 'auto', fontSize: 11, color: '#b0a090' }}>
                      {edge.created_at ? new Date(edge.created_at).toLocaleDateString('es-ES') : '—'}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Audit */}
        {tab === 'audit' && (
          <div style={{ background: '#fff', border: '1px solid #e2dbd0', borderRadius: 14, padding: 24 }}>
            <div style={{ fontFamily: 'Georgia,serif', fontSize: 16, marginBottom: 16 }}>Log de auditoría</div>
            {!selected ? (
              <div style={{ textAlign: 'center', color: '#7a7060', padding: 40 }}>Selecciona un agente para ver su log</div>
            ) : auditLog.length === 0 ? (
              <div style={{ textAlign: 'center', color: '#7a7060', padding: 40 }}>Sin entradas de auditoría</div>
            ) : auditLog.map((log, i) => (
              <div key={i} style={{ padding: '10px 0', borderBottom: '1px solid #f0ebe2', fontSize: 13 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                  <span style={{ fontWeight: 700, color: '#c94a1e' }}>{log.action}</span>
                  <span style={{ fontSize: 11, color: '#b0a090' }}>
                    {log.created_at ? new Date(log.created_at).toLocaleString('es-ES') : '—'}
                  </span>
                </div>
                {log.details && (
                  <div style={{ fontSize: 11, color: '#7a7060', fontFamily: 'monospace' }}>
                    {typeof log.details === 'string' ? log.details : JSON.stringify(log.details)}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Agent detail panel */}
        {selected && (
          <div style={{ background: '#fff', border: '1px solid #e2dbd0', borderRadius: 14, padding: 22, height: 'fit-content', position: 'sticky', top: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
              <div style={{ fontFamily: 'Georgia,serif', fontSize: 16 }}>{selected.full_name}</div>
              <button onClick={() => setSelected(null)} style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: 18, color: '#7a7060' }}>✕</button>
            </div>

            <TrustBadge level={selected.trust_level || 0} />

            {selectedScore && (
              <div style={{ marginTop: 16 }}>
                <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '.07em', textTransform: 'uppercase', color: '#7a7060', marginBottom: 10 }}>Trust Score</div>
                <ScoreBar score={selectedScore.trust_score} />

                <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {Object.entries(selectedScore.components || {}).map(([k, v]) => (
                    <div key={k} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                      <span style={{ color: '#7a7060' }}>{k.replace(/_/g, ' ')}</span>
                      <span style={{ fontWeight: 600, color: k.includes('penalty') ? '#9b2c5e' : '#2d6a4f' }}>
                        {k.includes('penalty') ? `-${v}` : `+${v}`}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Eligibility for next level */}
            {eligibility && (selected.trust_level || 0) < 4 && (
              <div style={{ marginTop: 16, background: '#f9f6f1', borderRadius: 10, padding: '12px 14px' }}>
                <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '.07em', textTransform: 'uppercase', color: '#7a7060', marginBottom: 10 }}>
                  Requisitos Nivel {eligibility.target_level}
                </div>
                {Object.entries(eligibility.checks || {}).map(([k, v]) => (
                  <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: 12 }}>
                    <span style={{ color: '#7a7060' }}>{k.replace(/_/g, ' ')}</span>
                    <span style={{ fontWeight: 700, color: v.ok ? '#2d6a4f' : '#9b2c5e' }}>
                      {v.ok ? '✓' : `${v.current || 0}/${v.required}`}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {/* Actions */}
            <div style={{ marginTop: 16, display: 'flex', gap: 8, flexDirection: 'column' }}>
              {eligibility?.eligible && (selected.trust_level || 0) < 4 && (
                <button onClick={handlePromote} style={{ padding: '10px', borderRadius: 8, border: 'none', background: '#2d6a4f', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                  ⬆ Promover a Nivel {(selected.trust_level || 0) + 1}
                </button>
              )}
              {(selected.trust_level || 0) > 0 && (
                <button onClick={handleDemote} style={{ padding: '10px', borderRadius: 8, border: '1px solid #9b2c5e', background: '#faeef4', color: '#9b2c5e', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                  ⬇ Degradar nivel
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
